import type { AppearanceOptions } from '@stripe/connect-js'

import { ADMIN_FONT_FAMILY } from '@/lib/admin-font'

const stripeConnectThemeDefaults = {
  light: {
    background: 'rgb(255, 255, 255)',
    formBackground: 'rgb(255, 255, 255)',
    offsetBackground: 'rgb(244, 244, 245)',
    text: 'rgb(65, 64, 66)',
    secondaryText: 'rgb(92, 91, 94)',
    placeholderText: 'rgb(92, 91, 94)',
    interaction: 'rgb(104, 79, 111)',
    action: 'rgb(130, 101, 138)',
    actionForeground: 'rgb(255, 255, 255)',
    secondaryBackground: 'rgb(255, 255, 255)',
    secondaryForeground: 'rgb(65, 64, 66)',
    border: 'rgb(211, 211, 213)',
    subtleBorder: 'rgb(231, 231, 232)',
    danger: 'rgb(147, 59, 71)',
    dangerForeground: 'rgb(255, 255, 255)',
    neutralBadgeBackground: 'rgb(244, 244, 245)',
    neutralBadgeText: 'rgb(92, 91, 94)',
    neutralBadgeBorder: 'rgb(231, 231, 232)',
    successBadgeBackground: 'rgb(235, 245, 238)',
    successBadgeText: 'rgb(61, 108, 78)',
    successBadgeBorder: 'rgb(185, 217, 196)',
    warningBadgeBackground: 'rgb(255, 244, 236)',
    warningBadgeText: 'rgb(140, 66, 21)',
    warningBadgeBorder: 'rgb(242, 197, 161)',
    dangerBadgeBackground: 'rgb(252, 242, 243)',
    dangerBadgeText: 'rgb(147, 59, 71)',
    dangerBadgeBorder: 'rgb(236, 191, 196)',
    overlayBackdrop: 'rgba(46, 45, 47, 0.48)',
  },
  dark: {
    background: 'rgb(49, 44, 50)',
    formBackground: 'rgb(49, 44, 50)',
    offsetBackground: 'rgb(58, 53, 59)',
    text: 'rgb(236, 233, 236)',
    secondaryText: 'rgb(179, 173, 180)',
    placeholderText: 'rgb(179, 173, 180)',
    interaction: 'rgb(193, 174, 199)',
    action: 'rgb(130, 101, 138)',
    actionForeground: 'rgb(255, 255, 255)',
    secondaryBackground: 'rgb(49, 44, 50)',
    secondaryForeground: 'rgb(236, 233, 236)',
    border: 'rgb(74, 69, 75)',
    subtleBorder: 'rgb(59, 54, 60)',
    danger: 'rgb(220, 149, 157)',
    dangerForeground: 'rgb(28, 27, 29)',
    neutralBadgeBackground: 'rgb(58, 53, 59)',
    neutralBadgeText: 'rgb(179, 173, 180)',
    neutralBadgeBorder: 'rgb(59, 54, 60)',
    successBadgeBackground: 'rgb(35, 57, 44)',
    successBadgeText: 'rgb(121, 177, 145)',
    successBadgeBorder: 'rgb(53, 84, 63)',
    warningBadgeBackground: 'rgb(68, 45, 31)',
    warningBadgeText: 'rgb(243, 178, 122)',
    warningBadgeBorder: 'rgb(99, 67, 44)',
    dangerBadgeBackground: 'rgb(73, 36, 42)',
    dangerBadgeText: 'rgb(229, 154, 162)',
    dangerBadgeBorder: 'rgb(107, 55, 62)',
    overlayBackdrop: 'rgba(18, 17, 19, 0.78)',
  },
}

function normalizeCssColorValue(value: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback
  }

  if (typeof navigator !== 'undefined' && /\bjsdom\b/i.test(navigator.userAgent)) {
    return /^(#|rgb|rgba|hsl|hsla)/i.test(value) ? value : fallback
  }

  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1

  const context = canvas.getContext('2d')

  if (!context) {
    return fallback
  }

  try {
    context.clearRect(0, 0, 1, 1)
    context.fillStyle = value
    context.fillRect(0, 0, 1, 1)
  } catch {
    return fallback
  }

  const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data

  if (alpha === 255) {
    return `rgb(${red}, ${green}, ${blue})`
  }

  return `rgba(${red}, ${green}, ${blue}, ${(Number(alpha) / 255).toFixed(3)})`
}

function resolveThemeColorValue(variableName: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback
  }

  const probe = document.createElement('div')
  probe.style.color = `var(${variableName})`
  probe.style.position = 'absolute'
  probe.style.opacity = '0'
  probe.style.pointerEvents = 'none'
  document.body.appendChild(probe)
  const resolvedValue = window.getComputedStyle(probe).color
  probe.remove()

  if (!resolvedValue) {
    return fallback
  }

  return normalizeCssColorValue(resolvedValue, fallback)
}

function resolveThemeFontFamily(fallback: string = ADMIN_FONT_FAMILY): string {
  if (typeof document === 'undefined') {
    return fallback
  }

  const fontFamily = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue('--font-sans')
    .trim()

  return fontFamily || fallback
}

export function buildStripeConnectAppearance(
  theme: string | null | undefined,
  { subtleBorder = false }: { subtleBorder?: boolean } = {}
): AppearanceOptions {
  const defaults = stripeConnectThemeDefaults[theme === 'dark' ? 'dark' : 'light']
  const borderVariable = subtleBorder ? '--border-subtle' : '--input'
  const borderFallback = subtleBorder ? defaults.subtleBorder : defaults.border

  return {
    overlays: 'drawer',
    variables: {
      fontFamily: resolveThemeFontFamily(),
      fontSizeBase: '16px',
      spacingUnit: '8px',
      borderRadius: '10px',
      colorPrimary: resolveThemeColorValue('--action', defaults.action),
      colorBackground: resolveThemeColorValue('--card', defaults.background),
      colorText: resolveThemeColorValue('--foreground', defaults.text),
      colorDanger: resolveThemeColorValue('--destructive', defaults.danger),

      buttonPrimaryColorBackground: resolveThemeColorValue('--action', defaults.action),
      buttonPrimaryColorBorder: resolveThemeColorValue('--action', defaults.action),
      buttonPrimaryColorText: resolveThemeColorValue(
        '--action-foreground',
        defaults.actionForeground
      ),
      buttonSecondaryColorBackground: resolveThemeColorValue(
        '--card',
        defaults.secondaryBackground
      ),
      buttonSecondaryColorBorder: resolveThemeColorValue('--input', defaults.border),
      buttonSecondaryColorText: resolveThemeColorValue(
        '--secondary-foreground',
        defaults.secondaryForeground
      ),
      buttonDangerColorBackground: resolveThemeColorValue('--destructive', defaults.danger),
      buttonDangerColorBorder: resolveThemeColorValue('--destructive', defaults.danger),
      buttonDangerColorText: resolveThemeColorValue(
        '--destructive-foreground',
        defaults.dangerForeground
      ),
      buttonPaddingX: '20px',
      buttonPaddingY: '10px',
      buttonBorderRadius: '24px',
      buttonLabelFontSize: '14px',
      buttonLabelFontWeight: '500',
      buttonLabelTextTransform: 'none',

      colorSecondaryText: resolveThemeColorValue('--muted-foreground', defaults.secondaryText),
      actionPrimaryColorText: resolveThemeColorValue('--interaction', defaults.interaction),
      actionPrimaryTextDecorationLine: 'underline',
      actionPrimaryTextDecorationColor: resolveThemeColorValue(
        '--interaction',
        defaults.interaction
      ),
      actionPrimaryTextDecorationStyle: 'solid',
      actionPrimaryTextDecorationThickness: '1px',
      actionPrimaryTextTransform: 'none',
      actionSecondaryColorText: resolveThemeColorValue('--foreground', defaults.text),
      actionSecondaryTextDecorationLine: 'underline',
      actionSecondaryTextDecorationColor: resolveThemeColorValue('--foreground', defaults.text),
      actionSecondaryTextDecorationStyle: 'solid',
      actionSecondaryTextDecorationThickness: '1px',
      actionSecondaryTextTransform: 'none',

      badgeNeutralColorBackground: resolveThemeColorValue(
        '--muted',
        defaults.neutralBadgeBackground
      ),
      badgeNeutralColorText: resolveThemeColorValue(
        '--muted-foreground',
        defaults.neutralBadgeText
      ),
      badgeNeutralColorBorder: resolveThemeColorValue(
        '--border-subtle',
        defaults.neutralBadgeBorder
      ),
      badgeSuccessColorBackground: resolveThemeColorValue(
        '--status-success-background',
        defaults.successBadgeBackground
      ),
      badgeSuccessColorText: resolveThemeColorValue(
        '--status-success-foreground',
        defaults.successBadgeText
      ),
      badgeSuccessColorBorder: resolveThemeColorValue(
        '--status-success-border',
        defaults.successBadgeBorder
      ),
      badgeWarningColorBackground: resolveThemeColorValue(
        '--status-warning-background',
        defaults.warningBadgeBackground
      ),
      badgeWarningColorText: resolveThemeColorValue(
        '--status-warning-foreground',
        defaults.warningBadgeText
      ),
      badgeWarningColorBorder: resolveThemeColorValue(
        '--status-warning-border',
        defaults.warningBadgeBorder
      ),
      badgeDangerColorBackground: resolveThemeColorValue(
        '--status-destructive-background',
        defaults.dangerBadgeBackground
      ),
      badgeDangerColorText: resolveThemeColorValue(
        '--status-destructive-foreground',
        defaults.dangerBadgeText
      ),
      badgeDangerColorBorder: resolveThemeColorValue(
        '--status-destructive-border',
        defaults.dangerBadgeBorder
      ),
      badgePaddingX: '10px',
      badgePaddingY: '2px',
      formBorderRadius: '10px',
      badgeBorderRadius: '24px',
      badgeLabelFontSize: '12px',
      badgeLabelFontWeight: '500',
      badgeLabelTextTransform: 'none',

      offsetBackgroundColor: resolveThemeColorValue('--muted', defaults.offsetBackground),
      formBackgroundColor: resolveThemeColorValue('--card', defaults.formBackground),
      colorBorder: resolveThemeColorValue(borderVariable, borderFallback),
      formHighlightColorBorder: resolveThemeColorValue('--ring', defaults.interaction),
      formAccentColor: resolveThemeColorValue('--action', defaults.action),
      formPlaceholderTextColor: resolveThemeColorValue(
        '--muted-foreground',
        defaults.placeholderText
      ),
      inputFieldPaddingX: '12px',
      inputFieldPaddingY: '10px',
      tableRowPaddingY: '10px',

      overlayBorderRadius: '24px',
      overlayZIndex: 1000,
      overlayBackdropColor: defaults.overlayBackdrop,

      bodyMdFontSize: '16px',
      bodyMdFontWeight: '400',
      bodySmFontSize: '14px',
      bodySmFontWeight: '400',
      headingXlFontSize: '31px',
      headingXlFontWeight: '600',
      headingXlTextTransform: 'none',
      headingLgFontSize: '25px',
      headingLgFontWeight: '600',
      headingLgTextTransform: 'none',
      headingMdFontSize: '21px',
      headingMdFontWeight: '600',
      headingMdTextTransform: 'none',
      headingSmFontSize: '18px',
      headingSmFontWeight: '600',
      headingSmTextTransform: 'none',
      headingXsFontSize: '14px',
      headingXsFontWeight: '600',
      headingXsTextTransform: 'none',
      labelMdFontSize: '14px',
      labelMdFontWeight: '500',
      labelMdTextTransform: 'none',
      labelSmFontSize: '12px',
      labelSmFontWeight: '500',
      labelSmTextTransform: 'none',
    },
  }
}
